import type { ReactNode } from 'react'

export interface SectionHeadingProps {
  /**
   * The eyebrow above the title. **Optional, and it always was** — it was declared
   * required, but `ReactNode` includes `undefined`, so omitting it typechecked and
   * rendered an empty `<span class="ul-section-sub-title">` — an inline-block with a
   * bottom margin, pushing the title down by a line nobody asked for. At the time it also
   * drew the clip-path heart that used to hang off `::before`, which is how `/contact`
   * found it on 2026-09-21. The type says what was already true, and the render below
   * stops emitting the span at all.
   */
  subTitle?: ReactNode
  title: ReactNode
  /** Optional `.ul-section-descr` paragraph beneath the title. */
  descr?: ReactNode
  /**
   * Extra classes on the `<h2>` itself, e.g. `text-white` on the About page's tabs
   * heading. Passing a wrapper element instead would change the DOM, so this exists.
   */
  titleClassName?: string
  /** Modifiers on `.ul-section-heading`, e.g. `text-center justify-content-center`. */
  className?: string
  /**
   * Class for the inner wrapper. The source uses a bare `<div>` in most places and
   * `<div class="left">` in the blog section, so it is explicit rather than assumed.
   */
  innerClassName?: string
  /** Right-hand slot — a button or slider nav, depending on the section. */
  aside?: ReactNode
  /**
   * The heading level. `h2` everywhere except the first heading of a page with no banner,
   * which has to be the page's `<h1>`.
   *
   * **This exists because pages started losing their banners.** `Breadcrumb`'s title was
   * the `<h1>` on every page that had one, and as those came off over 2026-09-19 to 21 each
   * page's first section heading had to become the `<h1>` instead — `audit:seo` fails a page
   * with any number other than one. Pages had begun writing out this block's markup by hand
   * to get it, which is a shared block being copied, and that is what ground rule 5 and
   * `audit:reuse` exist to stop.
   *
   * The class stays `.ul-section-title` either way, because it is the design system's
   * contract and the size is the section's, not the level's.
   */
  titleTag?: 'h1' | 'h2'
}

/**
 * The `.ul-section-heading` block: eyebrow + title, optionally with a right-hand
 * action. Appears on nearly every page, always with this structure and varying only
 * by alignment modifiers, what sits in the aside, and — since the banners came off —
 * whether its title is the page's `<h1>`. See `titleTag`.
 */
export function SectionHeading({
  subTitle,
  title,
  descr,
  className,
  titleClassName,
  innerClassName,
  aside,
  titleTag: Title = 'h2',
}: SectionHeadingProps) {
  return (
    <div className={className ? `ul-section-heading ${className}` : 'ul-section-heading'}>
      <div {...(innerClassName ? { className: innerClassName } : {})}>
        {subTitle !== undefined && subTitle !== null && subTitle !== '' && (
          <span className="ul-section-sub-title">{subTitle}</span>
        )}
        <Title className={titleClassName ? `ul-section-title ${titleClassName}` : 'ul-section-title'}>
          {title}
        </Title>
        {descr && <p className="ul-section-descr">{descr}</p>}
      </div>
      {aside}
    </div>
  )
}
